import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import SearchInput from 'components/field/SearchInput';
import LordIcon from 'components/global/LordIcon';
import NoRecordsFound from 'components/global/NoRecordsFound';
import PageHeader from 'components/global/PageHeader';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import DynamicListPagination from 'components/list/DynamicListPagination';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { NOTIFICATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { THEME } from 'utils/Theme';

import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import NotificationDetailsModal from './NotificationDetailsModal';
import * as S from './Notifications.styled';
import { setAllAsRead, setNotificationRead, setNotificationsData } from './notificationsSlice';

/**
 * @author @omerrkosar
 */

export const notificationType = [
	{
		type: ['dataExport'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/exportData-icon.json.gz',
		bgColor: '#FFF5E9;',
		projectName: 'Export'
	},
	{
		type: ['importOrder', 'importNoSuccess'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz',
		bgColor: '#FFFAEE;',
		projectName: 'Import'
	},
	{
		type: [
			'orderNeedConfirmation',
			'returnWaitingConfirmation',
			'returnRequestReceived',
			'newOrder',
			'agedOrder',
			'orderSLABreach',
			'orderAssigned'
		],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/shopping-icon.json.gz',
		bgColor: '#F4F3FD;',
		projectName: 'Ecommerce'
	},
	{
		type: ['lowCredit', 'walletIncrease'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/payments-icon.json.gz',
		bgColor: '#E3F9EA;',
		projectName: 'payment'
	},
	{
		type: ['creditNotEnough', 'shipmentFailed', 'shipmentCreated'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/archive-icon.json.gz',
		bgColor: '#EAF6FF;',
		projectName: 'shipment'
	},
	{
		type: ['otoFlex'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/otoFlex-icon.json.gz',
		bgColor: '#E8F9FF;',
		projectName: 'otoFlex'
	},
	{
		type: ['otoPack'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/otoPack-icon.json.gz',
		bgColor: '#FDF1F1;',
		projectName: 'otoPack'
	},
	{
		type: ['subscription'],
		lordIcon:
			'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/manage-subscription-icon.json.gz',
		bgColor: '#E3FFFD;',
		projectName: 'subscription'
	},
	{
		type: ['default'],
		lordIcon: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/notification-icon.json.gz',
		bgColor: '#E3FFFD;',
		projectName: 'default'
	}
];

const Notification = ({ data }) => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const darkMode = useSelector((state) => state.layout.darkMode);
	const { getLocaleDateWithTime } = useLocale();
	const { showError } = useAlert();
	const targetRef = useRef(null);

	const handleReadNotification = async () => {
		try {
			const response = await axios.post(NOTIFICATION.UPDATE_NOTIFICATION_READ, { ID: data.ID });
			if (response.status === 200) {
				dispatch(setNotificationRead(data.ID));
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const handleDownload = useCallback(
		async (e) => {
			e.stopPropagation();
			const { name, link, date, translationObject } = JSON.parse(data.data);
			const blob = await (await fetch(link)).blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = `${t(name, translationObject)}_${t('export')}_${getLocaleDateWithTime(date)}.xlsx`
				.split(' ')
				.join('_');
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		},
		[data]
	);

	return (
		<S.NotificationCard
			rounded
			border={false}
			onClick={() => {
				NiceModal.show(NotificationDetailsModal, { selectedNotification: data });

				!data.isRead && handleReadNotification();
			}}>
			<div className="flex messageContainer" ref={targetRef}>
				<S.BoxIcon
					bgColor={
						notificationType.find((item) => item.type.includes(data.type))?.bgColor ||
						notificationType.find((item) => item.type.includes('default'))?.bgColor
					}>
					<LordIcon
						primaryColor={THEME.BLACK}
						secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
						width={'2.6rem'}
						height={'2.6rem'}
						target={targetRef}
						src={
							notificationType.find((item) => item.type.includes(data.type))?.lordIcon ||
							notificationType.find((item) => item.type.includes('default'))?.lordIcon
						}
					/>
				</S.BoxIcon>
				<div className="content">
					{data.header && (
						<Typography className="mb-2" fontWeight="700" color="var(--black)">
							{data.header}
						</Typography>
					)}
					{data.content && (
						<Typography
							style={{ color: 'var(--black)', maxWidth: '60rem' }}
							fontWeight="500"
							className="pb-1"
							gutterBottom
							noWrap>
							{data.content}
						</Typography>
					)}
					{data.date && (
						<Typography style={{ color: '#949494' }} variant="caption" noWrap>
							{getLocaleDateWithTime(data.date)}
						</Typography>
					)}
				</div>
				<div className="pt-5">{!data.isRead && <Badge size="standart" variant="dot" color="red" />}</div>
				{data?.data && typeof data.data === 'string' && JSON.parse(data.data)?.link && (
					<div className="mt-4">
						<Button label="download" variant="blueBorderless" onClick={handleDownload} />
					</div>
				)}
			</div>
		</S.NotificationCard>
	);
};
const Notifications = () => {
	const { size, items: notificationData } = useSelector((state) => state.notifications);
	const { unreadCount } = useSelector((state) => state.notifications);

	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showError } = useAlert();

	const [isLoading, setIsLoading] = useState(true);
	const [dateInterval, setDateInterval] = useState({});
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchValue, setSearchValue] = useState(null);
	const debouncedSearchValue = useDebounce(searchValue);

	const [filterArray, setFilterArray] = useState([
		{
			action: 'isNot',
			key: ['type'],
			value: ['export']
		},
		{
			action: 'between',
			from: dayjs().subtract(7, 'day').endOf('day').toISOString(),
			key: ['date'],
			to: dayjs().endOf('day').toISOString()
		}
	]);

	const markAllAsRead = async () => {
		setIsLoading(true);

		try {
			const response = await axios.post(NOTIFICATION.UPDATE_ALL_NOTIFICATION_READ, {});
			if (response.status === 200) {
				dispatch(setAllAsRead());
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}

		setIsLoading(false);
	};

	const getNotifications = async () => {
		setIsLoading(true);

		const requestJson = {
			paging: {
				offset: (currentPage - 1) * pageSize,
				count: pageSize
			},
			filter: filterArray,
			sort: {
				key: ['date'],
				order: 'desc'
			}
		};

		try {
			const response = await axios.post(NOTIFICATION.GET_NOTIFICATIONS, requestJson);
			if (response.status === 200) {
				dispatch(setNotificationsData(response.data));
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}

		setIsLoading(false);
	};

	useUpdateEffect(() => {
		if (debouncedSearchValue) {
			const indexofsearchFilter = filterArray?.findIndex((item) => item.action === 'contains');
			const filterArrayCopy = [...filterArray];

			if (indexofsearchFilter > -1) {
				filterArrayCopy[indexofsearchFilter].value = [searchValue];
			} else {
				filterArrayCopy.push({
					action: 'contains',
					key: ['content', 'header'],
					value: [searchValue]
				});
			}
			setFilterArray(filterArrayCopy);
		} else {
			setFilterArray(filterArray.filter((item) => item.action !== 'contains'));
		}
	}, [debouncedSearchValue]);

	const updateFilterArray = () => {
		const indexofDateFilter = filterArray?.findIndex((item) => item.action === 'between');
		const filterArrayCopy = [...filterArray];

		if (indexofDateFilter > -1) {
			filterArrayCopy[indexofDateFilter].from = dateInterval.start;
			filterArrayCopy[indexofDateFilter].to = dateInterval.end;
		} else {
			filterArrayCopy.push({
				action: 'between',
				key: ['date'],
				from: dateInterval.start,
				to: dateInterval.end
			});
		}
		setFilterArray(filterArrayCopy);
	};

	useUpdateEffect(() => {
		updateFilterArray();
	}, [dateInterval]);

	useEffect(() => {
		getNotifications();
	}, [pageSize, currentPage, filterArray]);

	return (
		<div>
			<PageHeader title="notifications">
				{unreadCount > 0 && (
					<Button
						label="markAllAsRead"
						icon={faCircleCheck}
						variant="blueBorderless"
						onClick={markAllAsRead}
						component="button"
						loading={isLoading}
					/>
				)}
			</PageHeader>

			<S.NotificationContainer>
				<S.FilterBar>
					<SearchInput
						style={{ width: '40rem' }}
						placeholder="searchNotifications"
						value={searchValue}
						setValue={setSearchValue}
					/>
					<DateFilter style={{ width: '30rem' }} defaultDay="last7Days" setValue={setDateInterval} />
				</S.FilterBar>
				{isLoading ? (
					<div>
						{[...Array(5)].map((_, i) => (
							<S.LoadingRow key={i}>
								<Skeleton width="15%" height="2rem" />
								<Skeleton width="25%" height="2rem" />
							</S.LoadingRow>
						))}
					</div>
				) : (
					<S.CardContainer>
						{notificationData?.length > 0 ? (
							notificationData?.map((data, i) => <Notification key={i} data={data} />)
						) : (
							<NoRecordsFound
								lottieName="no-notification"
								style={{ width: '30rem' }}
								title="noNotificationsInThisDatePeriod"
								subTitle="noNotificationsInThisDatePeriod.description"></NoRecordsFound>
						)}
					</S.CardContainer>
				)}
				<DynamicListPagination
					currentPage={currentPage}
					totalCount={size}
					pageSize={pageSize}
					onPageChange={setCurrentPage}
					setPageSize={setPageSize}
					containerStyle={{
						padding: 0
					}}
				/>
			</S.NotificationContainer>
		</div>
	);
};

export default Notifications;
